import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PlanForm from './PlanForm';
import { useT } from '../i18n/I18nContext';
import { useTrackSection } from '../hooks/useTrackSection';

// Embedded version of PlanForm, framed as a landing section. The form lives in
// its own card; this wrapper just gives it section padding, an intro headline,
// and a scroll-triggered reveal that matches the rest of the page.
export default function PlanFormSection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  useTrackSection('plan_form', sectionRef);
  const headRef    = useRef<HTMLDivElement>(null);
  const formRef    = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(headRef.current,
        { opacity: 0, y: 28 },
        { opacity: 1, y: 0, duration: 0.75, ease: 'power2.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 78%' } }
      );
      gsap.fromTo(formRef.current,
        { opacity: 0, y: 32 },
        { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: formRef.current, start: 'top 82%' } }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="quiz"
      style={{
        position: 'relative',
        padding: 'clamp(56px, 7vw, 96px) clamp(24px, 5vw, 80px)',
        zIndex: 1,
        background: 'transparent',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Section heading (lives outside the form card so the page-level
            hierarchy reads correctly when embedded). */}
        <div ref={headRef} style={{ textAlign: 'center', marginBottom: 'clamp(28px, 3.5vw, 44px)', opacity: 0 }}>
          <h2 style={{
            fontSize: 'clamp(26px, 3.2vw, 46px)',
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: '-0.022em',
            color: 'white',
            marginBottom: 14,
          }}>
            {t.planSection.titleA}
          </h2>
          <p style={{
            fontSize: 'clamp(14px, 1.6vw, 17px)',
            color: 'rgba(255,255,255,0.62)',
            lineHeight: 1.65,
            maxWidth: 540,
            margin: '0 auto',
          }}>
            {t.planSection.subtitle}
          </p>
        </div>

        {/* Form (its own page-level H1 hidden — section heading already serves that role) */}
        <div ref={formRef} style={{ opacity: 0 }}>
          <PlanForm hideHeading />
        </div>
      </div>
    </section>
  );
}
